import { isValid, parse } from "date-fns";

export type GameStartFields = {
  startDate: number;
  startTime?: string | null;
};

const DISPLAY_TIME_FORMATS = [
  "h:mm a",
  "hh:mm a",
  "H:mm",
  "HH:mm",
  "h:mm:ss a",
  "hh:mm:ss a",
];

/**
 * Kickoff instant for Live vs Upcoming: optional display time (e.g. "7:30 PM") on the same
 * calendar day as `startDate` (local), otherwise the stored `startDate` timestamp.
 */
export function getEffectiveGameStartMs(game: GameStartFields): number {
  const reference = new Date(game.startDate);
  const raw = game.startTime?.trim();
  if (!raw) return game.startDate;

  for (let i = 0; i < DISPLAY_TIME_FORMATS.length; i += 1) {
    const parsed = parse(raw, DISPLAY_TIME_FORMATS[i], reference);
    if (isValid(parsed)) return parsed.getTime();
  }

  const fallback = new Date(`${reference.toDateString()} ${raw}`);
  if (!Number.isNaN(fallback.getTime())) return fallback.getTime();

  return game.startDate;
}

/**
 * Start of the operator "game day" window in UTC (historically ~midnight US Eastern):
 * 05:00:00.000 UTC on the UTC calendar day of {@link startDateMs}.
 */
export function getGameLiveWindowStartUtcMs(startDateMs: number): number {
  const d = new Date(startDateMs);
  return Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    5,
    0,
    0,
    0,
  );
}

/**
 * When the operator dashboard should treat an unfinished game as Live (not Upcoming):
 * the earlier of {@link getGameLiveWindowStartUtcMs} and {@link getEffectiveGameStartMs}.
 * This matches evening kickoff to the scheduled time (see `getEffectiveGameStartMs`) while
 * still opening the dashboard on the game-day window for day games.
 */
export function getOperatorDashboardLiveTransitionMs(game: GameStartFields): number {
  return Math.min(
    getGameLiveWindowStartUtcMs(game.startDate),
    getEffectiveGameStartMs(game),
  );
}

type DashboardLiveFields = GameStartFields & { operatorMarkedLiveAt?: number | null };

/** Unfinished, non-archived games only — use after archived/finish checks. */
export function isOperatorDashboardLivePhase(
  game: DashboardLiveFields,
  nowMs: number = Date.now(),
): boolean {
  const marked = game.operatorMarkedLiveAt;
  if (marked != null && marked <= nowMs) {
    return true;
  }
  return nowMs >= getOperatorDashboardLiveTransitionMs(game);
}
